// src/admin/shell/Sidebar.tsx
// Two-section nav (Operations / System) with live queue badges, search trigger,
// system-status pill, and user card. Capability-gated entries are dropped via
// useRole.can() — same logic as the prior version, just reorganised visually.
//
// The aside collapses to a 72px icon rail when useSidebarCollapsed is on. CSS
// hides labels/badges/status text in that mode; we still pass title attrs so
// hovering reveals what each icon does.
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Wallet, Users, ShieldCheck, AlertTriangle,
  GitPullRequest, FileText, UserCog, Sliders, Search,
  ChevronRight, ChevronLeft, LogOut,
} from 'lucide-react';
import { useRole, type Capability } from '@admin/auth/useRole';
import { useAuth } from '@admin/auth/useAuth';
import { useDashboardStats } from '@admin/hooks/useDashboardStats';
import StatusDot from '@admin/components/StatusDot';
import Avatar from '@admin/components/Avatar';
import ThandizoLogo from '@admin/components/ThandizoLogo';
import { useSidebarCollapsed } from './useSidebarCollapsed';
import styles from './Sidebar.module.css';

interface NavEntry {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  cap?: Capability;
  badgeKey?: 'pending_approval' | 'kyc_pending' | 'delinquent' | 'action_requests_pending';
}

const OPERATIONS: NavEntry[] = [
  { to: '/',             label: 'Dashboard',       icon: LayoutDashboard },
  { to: '/loans',        label: 'Loans',           icon: Wallet,         cap: 'loans.read',       badgeKey: 'pending_approval' },
  { to: '/borrowers',    label: 'Borrowers',       icon: Users,          cap: 'borrowers.read' },
  { to: '/kyc',          label: 'KYC Review',      icon: ShieldCheck,    cap: 'kyc.read',         badgeKey: 'kyc_pending' },
  { to: '/collections',  label: 'Collections',     icon: AlertTriangle,  cap: 'collections.read', badgeKey: 'delinquent' },
  { to: '/actions',      label: 'Action Requests', icon: GitPullRequest, cap: 'actions.review',   badgeKey: 'action_requests_pending' },
];

const SYSTEM: NavEntry[] = [
  { to: '/audit',  label: 'Audit Log',     icon: FileText, cap: 'audit.read' },
  { to: '/admins', label: 'Admins',        icon: UserCog,  cap: 'admins.read' },
  { to: '/config', label: 'System Config', icon: Sliders,  cap: 'config.read' },
];

interface Props {
  onSearchClick: () => void;
}

export default function Sidebar({ onSearchClick }: Props) {
  const { can } = useRole();
  const { admin, signOut } = useAuth();
  const navigate = useNavigate();
  const { collapsed, toggle } = useSidebarCollapsed();
  const { data: stats } = useDashboardStats();

  const visible = (entries: NavEntry[]) =>
    entries.filter(e => e.cap === undefined || can(e.cap));

  const renderEntry = (entry: NavEntry) => {
    const Icon = entry.icon;
    const badge = entry.badgeKey && stats ? stats[entry.badgeKey] : 0;
    return (
      <NavLink
        key={entry.to}
        to={entry.to}
        end={entry.to === '/'}
        title={collapsed ? entry.label : undefined}
        className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
      >
        <Icon size={17} strokeWidth={1.7} />
        <span className={styles.linkLabel}>{entry.label}</span>
        {badge ? <span className={styles.badge}>{badge}</span> : null}
      </NavLink>
    );
  };

  return (
    <aside
      className={`${styles.sidebar} thandizo-scroll`}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <div className={styles.brandRow}>
        <ThandizoLogo size={collapsed ? 32 : 28} />
        <div className={styles.brandText}>
          <span className={styles.brandName}>Thandizo</span>
          <span className={styles.brandSub}>Admin Console</span>
        </div>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      <button
        type="button"
        className={styles.searchBtn}
        onClick={onSearchClick}
        title={collapsed ? 'Quick search (⌘K)' : undefined}
      >
        <Search size={14} aria-hidden="true" />
        <span>Quick search…</span>
        <kbd>⌘K</kbd>
      </button>

      <div className={styles.sectionLabel}>Operations</div>
      <nav className={styles.navList}>{visible(OPERATIONS).map(renderEntry)}</nav>

      {collapsed && <div className={styles.sectionDivider} aria-hidden="true" />}
      <div className={`${styles.sectionLabel} ${styles.next}`}>System</div>
      <nav className={styles.navList}>{visible(SYSTEM).map(renderEntry)}</nav>

      <div className={styles.spacer} />

      <div className={styles.statusPill} title={collapsed ? 'All systems operational' : undefined}>
        <div className={styles.row}>
          <StatusDot tone="success" pulse />
          <span>All systems operational</span>
        </div>
        <div className={styles.meta}>Mobile money · SMS · Auth</div>
      </div>

      {admin && (
        <div className={styles.userCard} title={collapsed ? `${admin.display_name} · ${admin.role}` : undefined}>
          <Avatar name={admin.display_name} size={collapsed ? 32 : 30} />
          <div className={styles.who}>
            <div className={styles.name}>{admin.display_name}</div>
            <div className={styles.role}>{admin.role.replace('_', ' ')}{admin.is_readonly ? ' · read-only' : ''}</div>
          </div>
          <button
            type="button"
            className={styles.signout}
            onClick={() => { void signOut().then(() => navigate('/login', { replace: true })); }}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
          <ChevronRight size={14} aria-hidden="true" className={styles.chev} />
        </div>
      )}
    </aside>
  );
}
